/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { v4 as uuidv4 } from 'uuid';
import parse from 'parse-json';
import { merge } from 'object-path-immutable';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import HeaderWrapper from '../Header/HeaderWrapper';

import { Headings } from '../Header/Header';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import AreaPlotChartStyles from './AreaPlotChart.styles';

interface highChartOptions {
    path?: string;
    value?: string;
}

interface legend {
    value?: string;
}

interface AreaPlotChartProps {
    options: highChartOptions[];
    legend: legend[];
    chartTitles: Headings[];
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, AreaPlotChartProps {}

const AreaPlotChart = (props: commonTypes.WidgetInstanceProps) => {
    const { documentType } = commonHooks.useAppContext();
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const { styles, options, legend, chartTitles } = widgetProperties || {};
    const { data } = props;
    const areaPlotChartId = uuidv4();

    const xLabels = (val: number) => {
        switch (val) {
            case -100:
                return 'Deep Value';
            case 0:
                return 'Core Value';
            case 100:
                return 'Core';
            case 200:
                return 'Core Growth';
            case 300:
                return 'High Growth';
        }
    };

    const yLabels = (val: number) => {
        switch (val) {
            case -100:
                return 'Micro';
            case 0:
                return 'Small';
            case 100:
                return 'Medium';
            case 200:
                return 'Large';
            case 300:
                return 'Giant';
        }
    };

    let highChartOptions = {
        chart: {
            type: 'scatter',
            height: 250,
            events: {
                load: function (this: any) {
                    const chart = this;
                    const series = chart.series[0].data[0];
                    this.renderer
                        .createElement('ellipse')
                        .attr({
                            cx: series.plotX,
                            cy: series.plotY,
                            rx: (60 / 100) * series.plotX,
                            ry: (30 / 100) * series.plotY,
                            'stroke-width': 1,
                            transform: 'rotate(10,100,100)',
                            stroke: '#BFCCC5',
                            fill: 'rgba(156, 218, 186, 0.5)',
                            zIndex: -1,
                        })
                        .add(chart.series[0].group);
                },
            },
        },
        title: {
            text: '',
        },
        credits: {
            enabled: false,
        },
        legend: {
            enabled: false,
        },
        xAxis: {
            labels: {
                align: 'left',
                formatter: function (this: any) {
                    return xLabels(this.value);
                },
            },
            gridLineWidth: 1,
            gridLineColor: '#000000',
            min: -100,
            max: 400,
            tickWidth: 0,
            tickInterval: 100,
            plotBands: [
                {
                    from: -100,
                    to: 0,
                    color: '#ededed',
                },
                {
                    from: 300,
                    to: 400,
                    color: '#ededed',
                },
            ],
        },
        yAxis: {
            opposite: true,
            min: -100,
            max: 400,
            tickInterval: 100,
            gridLineColor: '#000000',
            title: {
                text: '',
            },
            labels: {
                align: 'left',
                enabled: true,
                formatter: function (this: any) {
                    return yLabels(this.value);
                },
            },
            plotBands: [
                {
                    from: -100,
                    to: 0,
                    color: '#ededed',
                },
                {
                    from: 300,
                    to: 400,
                    color: '#ededed',
                },
            ],
        },
        series: [
            {
                data: [
                    {
                        x: 200,
                        y: 20,
                    },
                ],
                marker: {
                    radius: 10,
                    overflow: 'hidden',
                    fillColor: '#0AA759',
                },
            },
        ],
    };

    Array.isArray(options) &&
        options.forEach((o) => {
            let userOptions;

            try {
                userOptions = parse((o.value as string) || '{}');
            } catch (error) {
                userOptions = {};
            }

            highChartOptions = merge(highChartOptions, o.path, userOptions);
        });

    return (
        <>
            <div
                className={`area-plot-chart-container`}
                css={css`
                    ${AreaPlotChartStyles}${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles}
                `}
            >
                <HeaderWrapper headings={chartTitles} data={data} />
                <div id={`${areaPlotChartId}`} className="js-area-plot-chart-tile">
                    {['pdf', 'pptx'].includes(documentType) ? (
                        <div id={`${areaPlotChartId}-plot-chart`} />
                    ) : (
                        <HighchartsReact highcharts={Highcharts} options={highChartOptions} />
                    )}
                </div>
                <div className={`area-plot-chart-legend`}>
                    <table className="table--plain">
                        <thead className="sr-only">
                            <tr className="sr-only">
                                <th>{`swatch`}</th>
                                <th>{`value`}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {legend?.map((d: any, i: number) => {
                                return (
                                    <tr key={i}>
                                        <td>
                                            <span className={`color-swatch-${i}`} />
                                        </td>
                                        <td className={`value value-${i}`}>{d}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            {['pdf', 'pptx'].includes(documentType) && (
                <script
                    id={`${areaPlotChartId}-chart-options`}
                    type="text/json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(highChartOptions) }}
                ></script>
            )}
        </>
    );
};

export default AreaPlotChart;
